import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Images } from "../../../../../utils/imageConstants";
import type { MatchTab } from "./types/IMatches";
import LiveMatches from "./LiveMatches";
import UpcomingMatches from "./UpcomingMatches";
import PastMatches from "./PastMatches";
import EditMatchScheduleModal from "./EditMatchScheduleModal";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QUERY_KEY } from "../../../../../services/queryEndPoint";
import { Api } from "../../../../../services/interceptor";
import Calendar from "../../../../../components/Calendar";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectSelectedLeague } from "../../../../../redux/slice/selectedLeagueSlice";
import { getAuthToken, getGuestModeFromStorage } from "../../../../../helper/helper";
import { canScheduleMatchesInLeague } from "../../../../../utils/leagueAccess";
import { calendarYearsFromMatches, filterMatchesByDateRange, filterMatchesBySearchQuery, formatMatchesDateRangeLabel, startOfDay } from "./matchDateFilterUtils";
import type { IMatchesResponse } from "../../../types/home";

const MATCH_PAGE_SIZE = 6;
const MATCHES_MOBILE_MAX_PX = 768;
const MATCH_STATUS_BY_TAB: Record<MatchTab, "IN_PROGRESS" | "SCHEDULED" | "ENDED"> = {
  live: "IN_PROGRESS",
  upcoming: "SCHEDULED",
  past: "ENDED",
};

const Matches: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [viewList, setViewList] = useState(false);
  const [matchLive, setMatchLive] = useState<MatchTab>("live");
  const [hasAutoPickedInitialTab, setHasAutoPickedInitialTab] = useState(false);
  const [appliedDateRange, setAppliedDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [matchSearchQuery, setMatchSearchQuery] = useState("");
  const tournamentInfo = location.pathname.includes("/tournaments/team-info");
  const { tournamentId: urlTournamentId } = useParams<{ tournamentId?: string }>();
  const tournamentId = urlTournamentId || sessionStorage.getItem("tournamentId");
  const teamSnapshotId = sessionStorage.getItem("teamSnapshotId");

  // const tournamentName = sessionStorage.getItem("tournamentName");
  const token = getAuthToken();
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);
  const isGuestModeStorage = getGuestModeFromStorage();
  const isGuestMode = isGuestModeRedux || isGuestModeStorage;
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canScheduleMatches = canScheduleMatchesInLeague(selectedLeague);
  const [editScheduleMatchId, setEditScheduleMatchId] = useState<string | null>(null);
  const [isNarrowMatches, setIsNarrowMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(`(max-width: ${MATCHES_MOBILE_MAX_PX}px)`).matches);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MATCHES_MOBILE_MAX_PX}px)`);
    const sync = () => setIsNarrowMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const openEditMatch = useCallback((id: string) => {
    const t = typeof id === "string" ? id.trim() : "";
    if (t) setEditScheduleMatchId(t);
  }, []);

  const closeEditMatch = useCallback(() => setEditScheduleMatchId(null), []);

  const TABS: Array<{
    key: MatchTab;
    label: string;
  }> = [
    {
      key: "live",
      label: "LIVE",
    },
    {
      key: "upcoming",
      label: "UPCOMING",
    },
    {
      key: "past",
      label: "PAST",
    },
  ];

  const effectiveViewList = isNarrowMatches ? false : viewList;

  const matchesQueryEnabled = tournamentInfo ? Boolean(tournamentId && teamSnapshotId) : Boolean(tournamentId);

  const endpoint = tournamentInfo ? QUERY_KEY.matchByTeamAndStatus : QUERY_KEY.matchByTournamentIdAndStatusPageable;

  const buildMatchParams = useCallback(
    (tab: MatchTab, page: number, size: number): Record<string, string | number> => {
      const params: Record<string, string | number> = {
        status: MATCH_STATUS_BY_TAB[tab],
        page,
        size,
      };
      if (tournamentInfo) {
        params.teamId = teamSnapshotId ?? "";
      } else {
        params.tournamentId = tournamentId ?? "";
      }
      return params;
    },
    [teamSnapshotId, tournamentId, tournamentInfo],
  );

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [endpoint, matchLive, tournamentId, teamSnapshotId],
    enabled: matchesQueryEnabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const params = buildMatchParams(matchLive, pageParam, MATCH_PAGE_SIZE);
      const response = await Api.get<IMatchesResponse>(endpoint, { params });
      return response?.data ?? { matchResponseList: [] };
    },
    getNextPageParam: (lastPage, allPages) => {
      const list = lastPage?.matchResponseList ?? [];
      if (list.length < MATCH_PAGE_SIZE) return undefined;
      return allPages.length;
    },
  });

  const tabDataAvailabilityQueries = useQueries({
    queries: TABS.map(({ key }) => ({
      queryKey: [endpoint, "tab-availability", key, tournamentId, teamSnapshotId],
      enabled: matchesQueryEnabled,
      queryFn: async () => {
        const response = await Api.get<IMatchesResponse>(endpoint, {
          params: buildMatchParams(key, 0, 1),
        });
        return response?.data ?? { matchResponseList: [] };
      },
      staleTime: 30_000,
    })),
  });

  useEffect(() => {
    if (!matchesQueryEnabled) return;
    if (hasAutoPickedInitialTab) return;

    const allAvailabilityFetched = tabDataAvailabilityQueries.every((q) => q.isFetched || q.isError);
    if (!allAvailabilityFetched) return;

    const tabsWithData = TABS.filter((_, index) => {
      const count = tabDataAvailabilityQueries[index]?.data?.matchResponseList?.length ?? 0;
      return count > 0;
    });

    if (tabsWithData.length === 0 || tabsWithData.length === TABS.length) {
      setHasAutoPickedInitialTab(true);
      return;
    }

    const firstTabWithData = tabsWithData[0]?.key;
    if (firstTabWithData && firstTabWithData !== matchLive) {
      setMatchLive(firstTabWithData);
    }
    setHasAutoPickedInitialTab(true);
  }, [TABS, hasAutoPickedInitialTab, matchLive, matchesQueryEnabled, tabDataAvailabilityQueries]);

  useEffect(() => {
    setHasAutoPickedInitialTab(false);
    setMatchLive("live");
  }, [tournamentId, teamSnapshotId, tournamentInfo]);

  const matches = useMemo(() => infiniteData?.pages.flatMap((p) => p.matchResponseList ?? []) ?? [], [infiniteData]);

  useEffect(() => {
    setAppliedDateRange(null);
    setMatchSearchQuery("");
  }, [matchLive]);

  const filteredMatches = useMemo(() => {
    const byDate = filterMatchesByDateRange(matches, appliedDateRange);
    return filterMatchesBySearchQuery(byDate, matchSearchQuery);
  }, [matches, appliedDateRange, matchSearchQuery]);

  const dateRangeButtonLabel = useMemo(() => formatMatchesDateRangeLabel(appliedDateRange, matches), [appliedDateRange, matches]);

  const calendarYears = useMemo(() => calendarYearsFromMatches(matches), [matches]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {open && <div style={{ position: "fixed", inset: 0, zIndex: 2 }} onClick={() => setOpen(false)} aria-hidden="true" />}
      <EditMatchScheduleModal
        open={Boolean(editScheduleMatchId?.trim())}
        onOpenChange={(v) => {
          if (!v) closeEditMatch();
        }}
        matchId={editScheduleMatchId}
        tournamentId={tournamentId}
        leagueId={selectedLeague?.leagueId ?? null}
        matchType={sessionStorage.getItem("tournamentMatchType") ?? ""}
        tournamentCategory={sessionStorage.getItem("tournamentCategory") ?? ""}
      />
      {/* <div className={!tournamentInfo?"main-match-wrapper":""}>
        <div className={!tournamentInfo?"custom-container main-middle-content-wrap":""}>
          {!tournamentInfo &&
          <BreadCrumb />
             }
          <div className={!tournamentInfo ?"match-bg-wrapper":""}>
            {!tournamentInfo &&
            <div className="images-name-wrapper">
                <p className="match-name">
                  {tournamentName || "-"}
                </p>
            </div>
              }
            <div className={!tournamentInfo ?"matches-wrapper-container":"team-info-matches-wrapper-container"}> */}
      <div className={`matches-header${isNarrowMatches ? " matches-header--narrow" : ""}`}>
        {!tournamentInfo && <div className="matches-main-header">{/* <h1 className="heading">Matches</h1> */}</div>}
        <div className="toolbar">
          <div className="tabs" role="tablist" aria-label="Match Tabs">
            {TABS.map(({ key, label }) => {
              const isActive = matchLive === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    setHasAutoPickedInitialTab(true);
                    setMatchLive(key);
                  }}
                  className={`chip ${isActive ? "active" : ""}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="right-filters-data">
            <div className="controls">
              <div className="search">
                <input type="search" placeholder="Search Matches..." value={matchSearchQuery} onChange={(e) => setMatchSearchQuery(e.target.value)} aria-label="Search matches by team, venue, or tournament" autoComplete="off" />
              </div>
            </div>
            <div className="date-calendar">
              <button className="date-calendar-filter" type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="dialog">
                <span>{dateRangeButtonLabel}</span>
                <img src={Images.dropdown_filter_icon} alt="" />
              </button>

              {open && (
                <div className="calendar-popover" role="dialog" aria-label="Choose date range">
                  <Calendar
                    key={`${appliedDateRange?.start?.getTime() ?? "x"}-${appliedDateRange?.end?.getTime() ?? "y"}`}
                    initialStartDate={appliedDateRange?.start ?? null}
                    initialEndDate={appliedDateRange?.end ?? null}
                    years={calendarYears}
                    onApply={(range) => {
                      const s = range.startDate;
                      const e = range.endDate ?? range.startDate;
                      if (!s && !e) {
                        setAppliedDateRange(null);
                      } else if (s) {
                        setAppliedDateRange({
                          start: startOfDay(s),
                          end: startOfDay(e ?? s),
                        });
                      }
                      setOpen(false);
                    }}
                  />
                </div>
              )}
            </div>
            <div className="filter-view-icons">
              <div className="matches-view-switch" role="group" aria-label="View mode">
                <button type="button" className={`matches-view-toggle ${!viewList ? "active" : ""}`} onClick={() => setViewList(false)} aria-label="Grid view" aria-pressed={!viewList}>
                  <img src={Images.card_show_icon} alt="" />
                </button>
                <button type="button" className={`matches-view-toggle ${viewList ? "active" : ""}`} onClick={() => setViewList(true)} aria-label="List view" aria-pressed={viewList}>
                  <img src={Images.list_view_icon} alt="" />
                </button>
              </div>
            </div>
            {token && !isGuestMode && canScheduleMatches && (
              <div className="button-wrap button-wrap--end">
                <button type="button" className="schdule-button" onClick={() => navigate("/tournaments/schedule-match")}>
                  <img src={Images.schedule_icon} alt="" />
                  <span>Schedule</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {matchLive === "live" && <LiveMatches matches={filteredMatches} isLoading={isLoading} viewList={effectiveViewList} />}
      {matchLive === "upcoming" && <UpcomingMatches matches={filteredMatches} isLoading={isLoading} viewList={effectiveViewList} canEditMatch={Boolean(token && !isGuestMode && canScheduleMatches)} onEditMatch={token && !isGuestMode && canScheduleMatches ? openEditMatch : undefined} />}
      {matchLive === "past" && <PastMatches matches={filteredMatches} isLoading={isLoading} viewList={effectiveViewList} />}
      {matchesQueryEnabled && hasNextPage && !isLoading && (
        <div className="matches-load-more-wrap">
          <button type="button" className="matches-load-more-btn" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
            {isFetchingNextPage ? "Loading…" : "Show more"}
          </button>
        </div>
      )}
      {/* </div>
          </div>
        </div>
      </div> */}
    </>
  );
};

export default Matches;
